import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../Config';

const VehiculeList = () => {
    const [vehicules, setVehicules] = useState([]);
    const [filteredVehicules, setFilteredVehicules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [user, setUser] = useState(null);
    
    // États pour les filtres
    const [filters, setFilters] = useState({
        search: '',
        marque: '',
        modele: '',
        statut: '',
        kilometrageMin: '',
        kilometrageMax: ''
    });

    // États pour les alertes
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('success'); // 'success', 'error', 'warning'
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [vehicleToDelete, setVehicleToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const token = localStorage.getItem('token');

    // Afficher une alerte personnalisée
    const showCustomAlert = (message, type = 'success') => {
        setAlertMessage(message);
        setAlertType(type);
        setShowAlert(true);
        
        // Masquer automatiquement après 5 secondes
        setTimeout(() => {
            setShowAlert(false);
        }, 5000);
    };

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            setUser(JSON.parse(userData));
        }
        fetchVehicules();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [vehicules, filters]);

    const fetchVehicules = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/vehicules`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setVehicules(response.data);
            setFilteredVehicules(response.data);
            setError(null);
        } catch (err) {
            setError('Erreur lors du chargement des véhicules');
            console.error('Erreur API:', err);
            showCustomAlert('Erreur lors du chargement des véhicules', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applyFilters = () => {
        let filtered = [...vehicules];

        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            filtered = filtered.filter(v => 
                v.numero?.toLowerCase().includes(searchLower) ||
                v.marque?.toLowerCase().includes(searchLower) ||
                v.modele?.toLowerCase().includes(searchLower) ||
                v.utilisateur?.name?.toLowerCase().includes(searchLower) ||
                v.utilisateur?.email?.toLowerCase().includes(searchLower)
            );
        }

        if (filters.marque) {
            filtered = filtered.filter(v => 
                v.marque?.toLowerCase() === filters.marque.toLowerCase()
            );
        }

        if (filters.modele) {
            filtered = filtered.filter(v => 
                v.modele?.toLowerCase().includes(filters.modele.toLowerCase())
            );
        }

        if (filters.statut) {
            if (filters.statut === 'affecte') {
                filtered = filtered.filter(v => v.utilisateur);
            } else if (filters.statut === 'non-affecte') {
                filtered = filtered.filter(v => !v.utilisateur);
            }
        }

        if (filters.kilometrageMin) {
            const min = parseInt(filters.kilometrageMin);
            filtered = filtered.filter(v => (v.km_actuel || 0) >= min);
        }

        if (filters.kilometrageMax) {
            const max = parseInt(filters.kilometrageMax);
            filtered = filtered.filter(v => (v.km_actuel || 0) <= max);
        }

        setFilteredVehicules(filtered);
    };

    const handleResetFilters = () => {
        setFilters({
            search: '',
            marque: '',
            modele: '',
            statut: '',
            kilometrageMin: '',
            kilometrageMax: ''
        });
        showCustomAlert('Filtres réinitialisés', 'success');
    };

    const uniqueMarques = [...new Set(vehicules.map(v => v.marque).filter(Boolean))];

    const confirmDelete = (vehicle) => {
        if (!user || !['admin', 'validateur', 'chef_equipe'].includes(user.role)) {
            showCustomAlert('Vous n\'avez pas les droits pour supprimer un véhicule', 'error');
            return;
        }
        setVehicleToDelete(vehicle);
        setShowDeleteConfirm(true);
    };

    const handleDelete = async () => {
        if (!vehicleToDelete) return;

        setDeleteLoading(true);
        try {
            await axios.delete(`${API_URL}/vehicules/${vehicleToDelete.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            showCustomAlert(`Véhicule ${vehicleToDelete.numero} supprimé avec succès`, 'success');
            fetchVehicules();
        } catch (err) {
            showCustomAlert('Erreur lors de la suppression du véhicule', 'error');
            console.error(err);
        } finally {
            setDeleteLoading(false);
            setShowDeleteConfirm(false);
            setVehicleToDelete(null);
        }
    };

    const canManage = user && ['admin', 'validateur', 'chef_equipe'].includes(user.role);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Chargement des véhicules...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen">
            {/* Alerte flottante */}
            {showAlert && (
                <div className="fixed top-4 right-4 z-50 max-w-md">
                    <div className={`rounded-lg shadow-lg border p-4 flex items-center justify-between transform transition-all duration-300 ${
                        alertType === 'success' ? 'bg-green-50 border-green-200' :
                        alertType === 'error' ? 'bg-red-50 border-red-200' :
                        'bg-yellow-50 border-yellow-200'
                    }`}>
                        <div className="flex items-center">
                            <div className={`mr-3 ${
                                alertType === 'success' ? 'text-green-500' :
                                alertType === 'error' ? 'text-red-500' :
                                'text-yellow-500'
                            }`}>
                                {alertType === 'success' ? (
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                ) : alertType === 'error' ? (
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                ) : (
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.282 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                )}
                            </div>
                            <div>
                                <p className={`font-medium ${
                                    alertType === 'success' ? 'text-green-800' :
                                    alertType === 'error' ? 'text-red-800' :
                                    'text-yellow-800'
                                }`}>
                                    {alertType === 'success' ? 'Succès' : 
                                     alertType === 'error' ? 'Erreur' : 
                                     'Attention'}
                                </p>
                                <p className={`text-sm ${
                                    alertType === 'success' ? 'text-green-700' :
                                    alertType === 'error' ? 'text-red-700' :
                                    'text-yellow-700'
                                }`}>
                                    {alertMessage}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowAlert(false)}
                            className={`ml-4 ${
                                alertType === 'success' ? 'text-green-500 hover:text-green-700' :
                                alertType === 'error' ? 'text-red-500 hover:text-red-700' :
                                'text-yellow-500 hover:text-yellow-700'
                            }`}
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Véhicules</h1>
                            <p className="text-gray-600 mt-1">Gestion de la flotte automobile</p>
                        </div>

                        {canManage && (
                            <Link
                                to="/vehicules/nouveau"
                                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                            >
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Ajouter un véhicule
                            </Link>
                        )}
                    </div>
                </div>

                {/* Message d'erreur principal */}
                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">Erreur</h3>
                                <div className="mt-1 text-sm text-red-700">{error}</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filtres */}
                <div className="bg-white rounded-lg border p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                            <svg className="w-5 h-5 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                            </svg>
                            <h2 className="font-medium text-gray-900">Filtres</h2>
                        </div>
                        <button
                            onClick={handleResetFilters}
                            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                        >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Réinitialiser
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                        {/* Recherche */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Recherche
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    name="search"
                                    value={filters.search}
                                    onChange={handleFilterChange}
                                    placeholder="Rechercher..."
                                    className="w-full pl-10 pr-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Marque */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Marque
                            </label>
                            <select
                                name="marque"
                                value={filters.marque}
                                onChange={handleFilterChange}
                                className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Toutes les marques</option>
                                {uniqueMarques.map(marque => (
                                    <option key={marque} value={marque}>
                                        {marque}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Modèle */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Modèle
                            </label>
                            <input
                                type="text"
                                name="modele"
                                value={filters.modele}
                                onChange={handleFilterChange}
                                placeholder="Modèle"
                                className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        {/* Statut */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Statut
                            </label>
                            <select
                                name="statut"
                                value={filters.statut}
                                onChange={handleFilterChange}
                                className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Tous les statuts</option>
                                <option value="affecte">Affecté</option>
                                <option value="non-affecte">Non affecté</option>
                            </select>
                        </div>
                    </div>

                    {/* Kilométrage */}
                    <div className="border-t pt-4">
                        <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
<svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 11l7-7 7 7M5 19l7-7 7 7" />
</svg>
                            Kilométrage
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm text-gray-600 mb-1">Min (km)</label>
                                <input
                                    type="number"
                                    name="kilometrageMin"
                                    value={filters.kilometrageMin}
                                    onChange={handleFilterChange}
                                    className="w-full px-3 py-2 border rounded"
                                    placeholder="0"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-600 mb-1">Max (km)</label>
                                <input
                                    type="number"
                                    name="kilometrageMax"
                                    value={filters.kilometrageMax}
                                    onChange={handleFilterChange}
                                    className="w-full px-3 py-2 border rounded"
                                    placeholder="500000"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Résumé */}
                    {Object.values(filters).some(value => value !== '') && (
                        <div className="mt-4 pt-4 border-t">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-gray-600">
                                    {filteredVehicules.length} véhicule{filteredVehicules.length !== 1 ? 's' : ''} trouvé{filteredVehicules.length !== 1 ? 's' : ''}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {filters.search && (
                                        <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                            "{filters.search}"
                                        </span>
                                    )}
                                    {filters.marque && (
                                        <span className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">
                                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                            </svg>
                                            {filters.marque}
                                        </span>
                                    )}
                                    {filters.statut && (
                                        <span className={`inline-flex items-center px-3 py-1 text-xs rounded-full ${
                                            filters.statut === 'affecte' 
                                                ? 'bg-green-100 text-green-800' 
                                                : 'bg-gray-100 text-gray-800'
                                        }`}>
                                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                {filters.statut === 'affecte' ? (
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                ) : (
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                                )}
                                            </svg>
                                            {filters.statut === 'affecte' ? 'Affecté' : 'Non affecté'}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Tableau */}
                <div className="bg-white rounded-lg border overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Numéro</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Marque</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Modèle</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Kilométrage</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Affectation</th>
                                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {filteredVehicules.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-4 py-12 text-center">
                                            <div className="text-gray-500">
                                                <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                <p className="mb-2">Aucun véhicule trouvé</p>
                                                <button
                                                    onClick={handleResetFilters}
                                                    className="text-blue-600 hover:text-blue-800 font-medium"
                                                >
                                                    Réinitialiser les filtres
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredVehicules.map((vehicule) => (
                                        <tr key={vehicule.id} className="hover:">
                                            <td className="px-4 py-3">
                                                <Link
                                                    to={`/vehicules/${vehicule.id}`}
                                                    className="font-medium text-blue-600 hover:text-blue-800 inline-flex items-center"
                                                >
                                                    {vehicule.numero}
                                                </Link>
                                            </td>
                                            <td className="px-4 py-3">
                                                {vehicule.marque || '—'}
                                            </td>
                                            <td className="px-4 py-3">
                                                {vehicule.modele || '—'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center">
                                                    {vehicule.km_actuel?.toLocaleString() || 0} km
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                {vehicule.utilisateur ? (
                                                    <div className="flex items-center">
                                                        <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                                                        <span>{vehicule.utilisateur?.nom || vehicule.utilisateur?.email}</span>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center">
                                                        <div className="w-2 h-2 rounded-full bg-gray-300 mr-2"></div>
                                                        <span className="text-gray-500">Non affecté</span>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex justify-end space-x-3">
                                                    <Link
                                                        to={`/vehicules/${vehicule.id}`}
                                                        className="inline-flex items-center text-blue-600 hover:text-blue-800"
                                                        title="Voir détails"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                        </svg>
                                                    </Link>
                                                    {canManage && (
                                                        <>
                                                            <Link
                                                                to={`/vehicules/${vehicule.id}/edit`}
                                                                className="inline-flex items-center text-gray-600 hover:text-gray-900"
                                                                title="Modifier"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                </svg>
                                                            </Link>
                                                            <button
                                                                onClick={() => confirmDelete(vehicule)}
                                                                className="inline-flex items-center text-red-600 hover:text-red-800"
                                                                title="Supprimer"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                </svg>
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Statistiques */}
                <div className="mt-6 text-sm text-gray-600">
                    <p>
                        Total : {vehicules.length} véhicule{vehicules.length !== 1 ? 's' : ''}
                        {filteredVehicules.length !== vehicules.length && 
                            ` (${filteredVehicules.length} filtré${filteredVehicules.length !== 1 ? 's' : ''})`
                        }
                    </p>
                </div>
            </div>

            {/* Modal de confirmation de suppression */}
            {showDeleteConfirm && vehicleToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg p-6 max-w-sm w-full">
                        <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
                            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.282 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                            Confirmer la suppression
                        </h3>
                        <p className="text-gray-600 text-center mb-2">
                            Êtes-vous sûr de vouloir supprimer le véhicule :
                        </p>
                        <p className="text-gray-800 font-medium text-center mb-4">
                            {vehicleToDelete.numero}
                        </p>
                        <p className="text-sm text-gray-500 text-center mb-6">
                            Cette action est irréversible.
                        </p>
                        <div className="flex space-x-3">
                            <button
                                onClick={() => {
                                    setShowDeleteConfirm(false);
                                    setVehicleToDelete(null);
                                }}
                                className="flex-1 py-2 border rounded text-gray-700 hover: transition-colors"
                                disabled={deleteLoading}
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={deleteLoading}
                                className="flex-1 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center"
                            >
                                {deleteLoading ? (
                                    <div className="flex items-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Suppression...
                                    </div>
                                ) : (
                                    'Supprimer'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VehiculeList;